import { ReactNode, useMemo } from "react";
import {
  PaymentMethodType,
  WorkspaceDetails,
  Task,
  TaskPriority,
  TaskSection,
  TaskStatus,
} from "@dewo/app/graphql/types";
import { inject } from "between";
import { usePermission } from "@dewo/app/contexts/PermissionsContext";
import { useWorkspace } from "../../workspace/hooks";
import { useWorkspacePaymentMethods } from "../../payment/hooks";

const Between = inject("0123456789");

export interface TaskSectionData {
  id: string;
  tasks: Task[];
  title: string;
  section?: TaskSection;
  button?: ReactNode;
}

export const STATUS_LABEL: Record<TaskStatus, string> = {
  [TaskStatus.COMMUNITY_SUGGESTIONS]: "Community Suggestions",
  [TaskStatus.BACKLOG]: "Backlog",
  [TaskStatus.TODO]: "To Do",
  [TaskStatus.IN_PROGRESS]: "In Progress",
  [TaskStatus.IN_REVIEW]: "In Review",
  [TaskStatus.DONE]: "Done",
};

export const getWorkspaceTaskStatuses = (
  workspaceDetails: WorkspaceDetails | undefined
) => {
  const keys = Object.keys(STATUS_LABEL) as TaskStatus[];
  if (!workspaceDetails) return keys;
  const { showCommunitySuggestions } = workspaceDetails.options ?? {};
  const statuses = keys.filter((status) => {
    if (status === TaskStatus.COMMUNITY_SUGGESTIONS) {
      return showCommunitySuggestions;
    }
    return true;
  });
  return statuses;
};

export const PRIORITY_LABEL: Record<TaskPriority, string> = {
  [TaskPriority.NONE]: "No Priority",
  [TaskPriority.LOW]: "Low Priority",
  [TaskPriority.MEDIUM]: "Medium Priority",
  [TaskPriority.HIGH]: "High Priority",
  [TaskPriority.URGENT]: "Urgent",
};

export function useShouldShowInlinePayButton(task: Task): boolean {
  const { workspace } = useWorkspace(task.workspaceId);
  const paymentMethods = useWorkspacePaymentMethods(task.workspaceId);
  const canManageWorkspace = usePermission("update", workspace);
  const unpaidRewards = useMemo(
    () => task.rewards.filter((r) => !r.payments.length),
    [task.rewards]
  );
  const paymentMethodTypes = useMemo(
    () =>
      new Set(
        paymentMethods
          ?.filter((pm) =>
            unpaidRewards.some((r) => r.token.networkId === pm.network.id)
          )
          .map((pm) => pm.type)
      ),
    [paymentMethods, unpaidRewards]
  );
  const hasBatchPayButNotSinglePay =
    paymentMethodTypes.has(PaymentMethodType.GNOSIS_SAFE) &&
    !paymentMethodTypes.has(PaymentMethodType.METAMASK);

  return (
    task.status === TaskStatus.DONE &&
    !!task.assignees.length &&
    !!unpaidRewards.length &&
    !!canManageWorkspace &&
    !hasBatchPayButNotSinglePay
  );
}

export function getSortKeyBetween<T>(
  itemAbove: T | undefined,
  itemBelow: T | undefined,
  getSortKey: (item: T) => string | undefined
): string {
  const [a, b] = [
    !!itemAbove ? getSortKey(itemAbove) ?? Between.lo : Between.lo,
    !!itemBelow
      ? getSortKey(itemBelow) ?? String(Date.now())
      : String(Date.now()),
  ].sort(Between.strord);

  if (a === b) return a;
  return Between.between(a, b);
}
